import React from "react";
import Logo from "@/components/Logo";
import { Instagram, Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { track } from "@/lib/analytics";

export default function Footer() {
  const { t } = useLang();
  const navItems = t("footer.nav_items");
  const legal = t("footer.legal");

  return (
    <footer
      id="contacto"
      data-testid="site-footer"
      className="relative bg-olive-900 text-cream-100"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 border-b border-cream-100/15 pb-16">
          <div className="lg:col-span-5">
            <Logo tone="light" />
            <p className="mt-8 font-serif italic text-2xl sm:text-3xl leading-snug text-cream-100/90 max-w-md">
              {t("footer.tagline")}
            </p>
            <p className="mt-6 text-sm text-cream-100/60 font-light max-w-md leading-relaxed">
              {t("footer.about")}
            </p>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-6">
              {t("footer.nav_title")}
            </div>
            <ul className="space-y-3 text-cream-100/80 font-light">
              {navItems.map((i) => (
                <li key={i.h}>
                  <a
                    href={i.h}
                    className="hover:text-gold transition-colors duration-200"
                  >
                    {i.l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-6">
              {t("footer.contact_title")}
            </div>
            <ul className="space-y-4 text-cream-100/85 font-light" data-testid="footer-contact">
              <li className="flex items-start gap-3">
                <Mail size={16} strokeWidth={1.2} className="mt-1 text-gold" />
                <a
                  href="mailto:info@cortijodesantamaria.com"
                  data-testid="footer-email"
                  onClick={() => track("contact_click", { channel: "email", location: "footer" })}
                  className="hover:text-gold transition-colors duration-200 break-all"
                >
                  info@cortijodesantamaria.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} strokeWidth={1.2} className="mt-1 text-gold" />
                <a
                  href="tel:+34626411029"
                  data-testid="footer-phone"
                  onClick={() => track("contact_click", { channel: "phone", location: "footer" })}
                  className="hover:text-gold transition-colors duration-200"
                >
                  +34 626 411 029
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Instagram size={16} strokeWidth={1.2} className="mt-1 text-gold" />
                <a
                  href="https://www.instagram.com/cortijo.santamaria/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-contact"
                  onClick={() => track("social_click", { channel: "instagram", location: "footer_contact" })}
                  className="hover:text-gold transition-colors duration-200"
                >
                  @cortijo.santamaria
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.2} className="mt-1 text-gold" />
                <a
                  href="https://share.google/DHVU40DKgz3p7UbUO"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-maps"
                  onClick={() => track("social_click", { channel: "google_maps", location: "footer" })}
                  className="hover:text-gold transition-colors duration-200"
                >
                  {t("footer.location")}
                  <br />
                  {t("footer.location_sub")}
                </a>
              </li>
            </ul>

            <div className="mt-8 flex items-center gap-4">
              <a
                href="https://www.instagram.com/cortijo.santamaria/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                onClick={() => track("social_click", { channel: "instagram", location: "footer" })}
                aria-label="Instagram Cortijo Santa María"
                className="w-10 h-10 inline-flex items-center justify-center border border-cream-100/25 hover:border-gold hover:text-gold text-cream-100/80 transition-colors duration-300"
              >
                <Instagram size={16} strokeWidth={1.2} />
              </a>
              <a
                href="mailto:info@cortijodesantamaria.com"
                data-testid="footer-mail-cta"
                className="text-[11px] uppercase tracking-[0.28em] text-cream-100/70 hover:text-gold transition-colors duration-200"
              >
                {t("footer.mail_cta")}
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-[11px] uppercase tracking-[0.24em] text-cream-100/50">
          <span>© {new Date().getFullYear()} Cortijo Santa María · {t("footer.copyright")}</span>
          <div className="flex flex-wrap gap-6">
            {legal.map((l) => (
              <a key={l.l} href={l.h} className="hover:text-gold transition-colors">
                {l.l}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
